import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { Select } from "../ui/components/Select";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherCalendar } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import { journalsAPI, fetchCurrencies, Currency } from "../services/api";
import { LocationPicker } from "../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";
import { truncateText } from "../shared/utils/formatters";
import {
  isApAccountType,
  isArAccountType,
  isBadDebtAccountTypeCandidate,
} from "../shared/utils/accountTypeHelpers";
import {
  getAllCustomers,
  getCustomerById,
  getInvoicesForCustomer,
} from "../services/customerApi";
import {
  getAllVendors,
  getVendorById,
  getBillsForVendor,
} from "../services/vendorApi";
import {
  SearchableDropdown,
  GroupedItems,
  ErrorBanner,
  useToast,
  ReviewRequestModal,
  ReviewStatusBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
  AccountNameDropdown,
} from "../shared/presentation/components";
import {
  useErrorHandling,
  useDropdownCache,
  useJournalReviewData,
  useEntityReviewStatus,
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../shared/presentation/hooks";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../shared/utils/date";
import { useAuth } from "../features/auth/presentation/hooks/useAuth";
import {
  getSaveAndPublishLabel,
  getStoredAccountingBackend,
} from "../config/accounting";

interface JournalLineItemForm {
  id: string;
  account_type: string;
  account_id: string;
  account_name: string;
  description: string;
  contact_id: string;
  contact_name: string;
  customer_or_vendor: "customer" | "vendor" | "";
  /** AP/AR journal: optional bill (accounts_payable) or invoice (accounts_receivable) reference */
  reference_id: string;
  reference_display: string;
  /** Balance of selected bill/invoice (for validation: line amount must not exceed this) */
  reference_balance?: number;
  /** Status of selected bill/invoice (e.g. overdue, open) for credit summary cache */
  reference_status?: string;
  debit: string;
  credit: string;
  /** Exchange rate when contact currency differs from journal currency (AP/AR only) */
  exchange_rate?: string;
}

const AP_AR_DESCRIPTION_MAX_LENGTH = 95;

const NewJournalEntry: React.FC = () => {
  const navigate = useNavigate();
  // Read location state to determine default recurring selection
  const location = useLocation();
  const defaultRecurring: string = location?.state?.defaultRecurringYes
    ? "yes"
    : "no";

  // Form state
  const [date, setDate] = useState("");
  // const [journalNumber, setJournalNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  // ZOUR-65 F4: the GST registration this journal is filed against. NULL until
  // the list loads; the effect below settles it on the primary.
  const [locationId, setLocationId] = useState<string | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [notes, setNotes] = useState("");
  const [reportingMethod, setReportingMethod] = useState<string>("both");
  const status = "under_review"; // New journals are always drafts

  // Journal mode: manual (exclude AP/AR from accounts) or ap_ar (only AP/AR + balance sheet)
  const [journalMode, setJournalMode] = useState<"manual" | "ap_ar">("manual");
  const isApArJournal = journalMode === "ap_ar";
  const saveAndPublishLabel = getSaveAndPublishLabel(getStoredAccountingBackend());

  // Recurring journal state
  const [isRecurring, setIsRecurring] = useState<string>(defaultRecurring);
  const [recurringJournalName, setRecurringJournalName] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("");
  const [recurringInterval, setRecurringInterval] = useState<number | "">("");
  const [recurringStartDate, setRecurringStartDate] = useState("");
  const [recurringEndDate, setRecurringEndDate] = useState("");
  const [recurringNeverExpires, setRecurringNeverExpires] = useState(false);

  // Line items state
  const [lineItems, setLineItems] = useState<JournalLineItemForm[]>([
    {
      id: "1",
      account_type: "",
      account_id: "",
      account_name: "",
      description: "",
      contact_id: "",
      contact_name: "",
      customer_or_vendor: "",
      reference_id: "",
      reference_display: "",
      debit: "",
      credit: "",
      exchange_rate: "",
    },
    {
      id: "2",
      account_type: "",
      account_id: "",
      account_name: "",
      description: "",
      contact_id: "",
      contact_name: "",
      customer_or_vendor: "",
      reference_id: "",
      reference_display: "",
      debit: "",
      credit: "",
      exchange_rate: "",
    },
  ]);
  const showBillInvoiceColumn = useMemo(
    () =>
      isApArJournal &&
      lineItems.some(
        (item) =>
          item.contact_id &&
          (item.account_type === "accounts_payable" ||
            item.account_type === "accounts_receivable"),
      ),
    [isApArJournal, lineItems],
  );

  // Dropdown options state
  // Cache for selected contacts (for display purposes)
  const [contactsCache, setContactsCache] = useState<Map<string, any>>(
    new Map(),
  );
  // ZOUR-65 F4: which of our GST registrations this journal belongs to.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  // Default to the primary registration, the way the manual bill page does, so
  // a journal always carries an answer rather than an empty control.
  useEffect(() => {
    if (locationId || !primaryLocation?.zoho_location_id) return;
    setLocationId(primaryLocation.zoho_location_id);
  }, [primaryLocation?.zoho_location_id, locationId]);

  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  // Error handling with new hook
  const {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearFieldError,
    hasLineItemError,
  } = useErrorHandling();

  // Dropdown cache hook
  const { getCached, setCached, clearCache } = useDropdownCache();

  // Toast notifications
  const { showSuccess, showError } = useToast();

  // Auth for review banner
  const { user } = useAuth();

  // Journal review data hook (for new journal, documentId is null)
  const {
    canCreate,
    canPushToZoho,
    canCreateRecurring,
    needsReviewForPushToZoho,
    needsReviewForPushToZohoWithoutAttachment,
    needsReviewForCreateRecurring,
    needsReviewForApArCreate,
    isDataLoading: isReviewDataLoading,
  } = useJournalReviewData({
    moduleCode: MODULE_CODES.JOURNAL,
    entityType: "journal",
    documentId: null,
    fetchDocument: async () => {
      throw new Error("Cannot fetch document for new journal");
    },
    isPostedToZoho: () => false,
    initialDocument: null,
  });

  // Closed month state
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthAction, setPendingClosedMonthAction] = useState<
    "save_as_draft" | "save_and_publish" | null
  >(null);

  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "save_as_draft" | "save_and_publish" | null
  >(null);
  const [reviewChangeDetails, setReviewChangeDetails] =
    useState<ChangeDetails | null>(null);
  const [reviewActionCode, setReviewActionCode] = useState<string>("");

  // Closed month check (journal date)
  const journalMonth = date ? dateToMonthYear(date) : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(journalMonth);
  const isAdminOrCFO = useIsAdminOrCFO();

  // Review status (won't show for new journals until after creation)
  const [createdJournalId, setCreatedJournalId] = useState<number | null>(null);
  const { review } = useEntityReviewStatus(
    createdJournalId ? "journal" : null,
    createdJournalId,
  );

  // File upload state
  const [attachments, setAttachments] = useState<File[]>([]);

  // Helper function to format account type display
  const formatAccountType = (accountType: string): string => {
    return accountType.replace(/_/g, " ").toUpperCase();
  };

  // Helper function to determine if account requires vendor (Accounts Payable)
  const shouldShowOnlyVendors = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts payable") ||
      accountName.toLowerCase().includes("account payable")
    );
  };

  // Helper function to determine if account requires customer (Accounts Receivable)
  const shouldShowOnlyCustomers = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts receivable") ||
      accountName.toLowerCase().includes("account receivable")
    );
  };

  // Helper function to check if contact is required for the account
  const isContactRequired = (accountName: string): boolean => {
    return (
      shouldShowOnlyVendors(accountName) || shouldShowOnlyCustomers(accountName)
    );
  };

  // Helper function to check if contact currency matches journal currency
  const isContactCurrencyMatching = (
    contact: any,
    isVendor: boolean,
  ): boolean => {
    if (!currencyId) return true; // If no currency selected, allow all

    if (isVendor) {
      // For vendors, check vendor.currency_id matches journal currency_id
      return contact.currency_id === currencyId;
    } else {
      // For customers, check customer.currency_id matches journal currency_id
      return contact.currency_code === currencyId;
    }
  };

  // True when contact's currency (from dropdown) differs from journal's currencyId → show exchange rate
  const isContactCurrencyDifferentFromJournal = (
    lineItem: JournalLineItemForm,
  ): boolean => {
    if (!currencyId || !lineItem.contact_id) return false;
    const contact = contactsCache.get(lineItem.contact_id);
    if (!contact) return false;
    const isVendor = lineItem.customer_or_vendor === "vendor";
    let contactCurrencyId: string | undefined;
    if (isVendor) {
      contactCurrencyId = contact.currency_id;
    } else {
      contactCurrencyId =
        contact.currency_id ??
        currencies.find((c) => c.currency_code === contact.currency_code)
          ?.currency_id;
    }
    if (!contactCurrencyId) return false;
    return contactCurrencyId !== currencyId;
  };

  const getContactCurrencyCodeForLineItem = (
    lineItem: JournalLineItemForm,
  ): string | null => {
    if (!lineItem.contact_id) return null;
    const contact = contactsCache.get(lineItem.contact_id);
    console.log("contact", contact);
    if (!contact) return null;
    return contact.currency_symbol;
  };

  const getContactCurrencyIdForLineItem = (
    lineItem: JournalLineItemForm,
  ): string | null => {
    if (!lineItem.contact_id) return null;
    const contact = contactsCache.get(lineItem.contact_id);
    if (!contact) return null;
    return contact.currency_id;
  };

  // Helper function to fetch and build grouped contacts for a line item (async)
  const fetchGroupedContacts = async (
    accountName: string,
    searchTerm: string,
    limit: number,
    offset: number,
    selectedContactId?: string,
    selectedContactName?: string,
    selectedContactType?: "customer" | "vendor",
  ): Promise<{
    items: GroupedItems<any>[];
    hasMore?: boolean;
  }> => {
    // Only cache the initial load (offset 0, no search)
    // Note: We also need to include currencyId in cache key since contacts are filtered by currency
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { accountName, searchTerm, limit, offset, currencyId };

    if (shouldCache) {
      const cached = getCached<{
        items: GroupedItems<any>[];
        hasMore?: boolean;
      }>("contacts", cacheKey);

      if (cached) {
        // Only ensure selected contact is in the cached results on initial load (offset === 0)
        if (
          offset === 0 &&
          selectedContactId &&
          selectedContactName &&
          selectedContactType
        ) {
          // Check if selected contact exists in cached results and find its position
          let targetGroup: GroupedItems<any> | undefined;
          let selectedIndex = -1;

          for (const group of cached.items) {
            selectedIndex = group.items.findIndex((contact: any) => {
              const contactId =
                contact.zoho_customerid || contact.zoho_vendorid;
              return contactId === selectedContactId;
            });
            if (selectedIndex >= 0) {
              targetGroup = group;
              break;
            }
          }

          // If selected contact exists in cache, move it to top for better UX
          if (targetGroup && selectedIndex >= 0) {
            if (selectedIndex > 0) {
              // Remove from current position and prepend to top
              const itemsCopy = [...targetGroup.items];
              const [selectedContact] = itemsCopy.splice(selectedIndex, 1);
              targetGroup.items = [selectedContact, ...itemsCopy];

              return {
                items: cached.items,
                hasMore: cached.hasMore,
              };
            }
            // Already at top, return cached as is
            return cached;
          }

          // Selected contact not found in cache
          if (!targetGroup || selectedIndex < 0) {
            // Check contacts cache first (pre-fetched contacts)
            const cachedContact = contactsCache.get(selectedContactId);
            if (cachedContact) {
              const groupLabel =
                selectedContactType === "customer" ? "Customers" : "Vendors";
              let targetGroup = cached.items.find(
                (g: GroupedItems<any>) => g.groupLabel === groupLabel,
              );

              if (!targetGroup) {
                targetGroup = { groupLabel, items: [], hasMore: false };
                cached.items.push(targetGroup);
              }

              // Remove contact from its current position if it exists, then prepend
              const contactId =
                cachedContact.zoho_customerid || cachedContact.zoho_vendorid;
              targetGroup.items = targetGroup.items.filter(
                (c: any) =>
                  (c.zoho_customerid || c.zoho_vendorid) !== contactId,
              );
              targetGroup.items = [cachedContact, ...targetGroup.items];

              return {
                items: cached.items,
                hasMore: cached.hasMore,
              };
            }

            // Try to fetch from backend
            try {
              let selectedContact: any = null;
              if (selectedContactType === "customer") {
                const response = await getCustomerById(selectedContactId);
                selectedContact = response.data;
              } else if (selectedContactType === "vendor") {
                const response = await getVendorById(selectedContactId);
                selectedContact = response.data;
              }

              if (selectedContact) {
                // Store in cache for future use
                setContactsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedContactId, selectedContact);
                  return newCache;
                });

                const groupLabel =
                  selectedContactType === "customer" ? "Customers" : "Vendors";
                let targetGroup = cached.items.find(
                  (g: GroupedItems<any>) => g.groupLabel === groupLabel,
                );

                if (!targetGroup) {
                  targetGroup = { groupLabel, items: [], hasMore: false };
                  cached.items.push(targetGroup);
                }

                // Remove contact from its current position if it exists, then prepend
                const contactId =
                  selectedContact.zoho_customerid ||
                  selectedContact.zoho_vendorid;
                targetGroup.items = targetGroup.items.filter(
                  (c: any) =>
                    (c.zoho_customerid || c.zoho_vendorid) !== contactId,
                );
                targetGroup.items = [selectedContact, ...targetGroup.items];

                return {
                  items: cached.items,
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected contact from backend, using synthetic item:",
                error,
              );
            }

            // Fallback: Create synthetic contact
            const syntheticContact: any =
              selectedContactType === "customer"
                ? {
                    zoho_customerid: selectedContactId,
                    customer_name: selectedContactName,
                    currency_code: currencyId,
                  }
                : {
                    zoho_vendorid: selectedContactId,
                    vendor_legal_name: selectedContactName,
                    currency_id: currencyId,
                  };

            const groupLabel =
              selectedContactType === "customer" ? "Customers" : "Vendors";
            let targetGroup = cached.items.find(
              (g: GroupedItems<any>) => g.groupLabel === groupLabel,
            );

            if (!targetGroup) {
              targetGroup = { groupLabel, items: [], hasMore: false };
              cached.items.push(targetGroup);
            }

            // Remove contact from its current position if it exists, then prepend
            targetGroup.items = targetGroup.items.filter(
              (c: any) =>
                (c.zoho_customerid || c.zoho_vendorid) !== selectedContactId,
            );
            targetGroup.items = [syntheticContact, ...targetGroup.items];

            return {
              items: cached.items,
              hasMore: cached.hasMore,
            };
          }
        }
        return cached;
      }
    }

    const showOnlyVendors = shouldShowOnlyVendors(accountName);
    const showOnlyCustomers = shouldShowOnlyCustomers(accountName);

    const groups: GroupedItems<any>[] = [];
    let overallHasMore = false;

    // Fetch customers if applicable
    if (!showOnlyVendors) {
      try {
        const customersResponse = await getAllCustomers({
          search: searchTerm,
          limit,
          offset,
        });

        let filteredCustomers = [];

        if (journalMode === "manual") {
          filteredCustomers = customersResponse.data.filter((customer) =>
            isContactCurrencyMatching(customer, false),
          );
        } else {
          filteredCustomers = customersResponse.data;
        }

        // const filteredCustomers = customersResponse.data.filter((customer) =>
        //   isContactCurrencyMatching(customer, false),
        // );
        // const filteredCustomers = customersResponse.data;
        if (filteredCustomers.length > 0) {
          groups.push({
            groupLabel: "Customers",
            items: filteredCustomers,
            hasMore: customersResponse.pagination?.has_more || false,
          });
        }
        // Track hasMore even if filtered results are empty
        // because next page might have matching items
        if (customersResponse.pagination?.has_more) {
          overallHasMore = true;
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    }

    // Fetch vendors if applicable
    if (!showOnlyCustomers) {
      try {
        const vendorsResponse = await getAllVendors({
          search: searchTerm,
          limit,
          offset,
        });

        let filteredVendors = [];
        if (journalMode === "manual") {
          filteredVendors = vendorsResponse.data.filter((vendor) =>
            isContactCurrencyMatching(vendor, true),
          );
        } else {
          filteredVendors = vendorsResponse.data;
        }
        // const filteredVendors = vendorsResponse.data.filter((vendor) =>
        //   isContactCurrencyMatching(vendor, true),
        // );
        // const filteredVendors = vendorsResponse.data;
        if (filteredVendors.length > 0) {
          groups.push({
            groupLabel: "Vendors",
            items: filteredVendors,
            hasMore: vendorsResponse.pagination?.has_more || false,
          });
        }
        // Track hasMore even if filtered results are empty
        // because next page might have matching items
        if (vendorsResponse.pagination?.has_more) {
          overallHasMore = true;
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    }

    // Only ensure selected contact is in results on initial load (offset === 0)
    if (
      offset === 0 &&
      selectedContactId &&
      selectedContactName &&
      selectedContactType
    ) {
      // Check if selected contact exists in results and find its position
      let targetGroup: GroupedItems<any> | undefined;
      let selectedIndex = -1;

      for (const group of groups) {
        selectedIndex = group.items.findIndex((contact: any) => {
          const contactId = contact.zoho_customerid || contact.zoho_vendorid;
          return contactId === selectedContactId;
        });
        if (selectedIndex >= 0) {
          targetGroup = group;
          break;
        }
      }

      // If selected contact exists in results, move it to top for better UX
      if (targetGroup && selectedIndex >= 0) {
        if (selectedIndex > 0) {
          // Remove from current position and prepend to top
          const itemsCopy = [...targetGroup.items];
          const [selectedContact] = itemsCopy.splice(selectedIndex, 1);
          targetGroup.items = [selectedContact, ...itemsCopy];
        }
        // Already at top or moved to top, continue
      } else if (!targetGroup || selectedIndex < 0) {
        // Selected contact not found in results
        // Check contacts cache first
        const cachedContact = contactsCache.get(selectedContactId);
        if (cachedContact) {
          const groupLabel =
            selectedContactType === "customer" ? "Customers" : "Vendors";
          let targetGroup = groups.find((g) => g.groupLabel === groupLabel);

          if (!targetGroup) {
            targetGroup = { groupLabel, items: [], hasMore: false };
            groups.push(targetGroup);
          }

          // Remove contact from its current position if it exists, then prepend
          const contactId =
            cachedContact.zoho_customerid || cachedContact.zoho_vendorid;
          targetGroup.items = targetGroup.items.filter(
            (c: any) => (c.zoho_customerid || c.zoho_vendorid) !== contactId,
          );
          targetGroup.items = [cachedContact, ...targetGroup.items];
        } else {
          // Try to fetch from backend
          try {
            let selectedContact: any = null;
            if (selectedContactType === "customer") {
              const response = await getCustomerById(selectedContactId);
              selectedContact = response.data;
            } else if (selectedContactType === "vendor") {
              const response = await getVendorById(selectedContactId);
              selectedContact = response.data;
            }

            if (selectedContact) {
              // Check currency match
              const currencyMatches =
                selectedContactType === "customer"
                  ? selectedContact.currency_code === currencyId
                  : selectedContact.currency_id === currencyId;

              if (currencyMatches || !currencyId) {
                // Store in cache for future use
                setContactsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedContactId, selectedContact);
                  return newCache;
                });

                const groupLabel =
                  selectedContactType === "customer" ? "Customers" : "Vendors";
                let targetGroup = groups.find(
                  (g) => g.groupLabel === groupLabel,
                );

                if (!targetGroup) {
                  targetGroup = { groupLabel, items: [], hasMore: false };
                  groups.push(targetGroup);
                }

                // Remove contact from its current position if it exists, then prepend
                const contactId =
                  selectedContact.zoho_customerid ||
                  selectedContact.zoho_vendorid;
                targetGroup.items = targetGroup.items.filter(
                  (c: any) =>
                    (c.zoho_customerid || c.zoho_vendorid) !== contactId,
                );
                // Prepend selected contact to the group
                targetGroup.items = [selectedContact, ...targetGroup.items];
              }
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected contact from backend, using synthetic item:",
              error,
            );
            // Fallback: Create synthetic contact
            const syntheticContact: any =
              selectedContactType === "customer"
                ? {
                    zoho_customerid: selectedContactId,
                    customer_name: selectedContactName,
                    currency_code: currencyId,
                  }
                : {
                    zoho_vendorid: selectedContactId,
                    vendor_legal_name: selectedContactName,
                    currency_id: currencyId,
                  };

            const groupLabel =
              selectedContactType === "customer" ? "Customers" : "Vendors";
            let targetGroup = groups.find((g) => g.groupLabel === groupLabel);

            if (!targetGroup) {
              targetGroup = { groupLabel, items: [], hasMore: false };
              groups.push(targetGroup);
            }

            // Remove contact from its current position if it exists, then prepend
            targetGroup.items = targetGroup.items.filter(
              (c: any) =>
                (c.zoho_customerid || c.zoho_vendorid) !== selectedContactId,
            );
            targetGroup.items = [syntheticContact, ...targetGroup.items];
          }
        }
      }
    } else if (offset > 0 && selectedContactId) {
      // For pagination loads, ensure selected contact is not duplicated
      // Remove selected contact from any group if it exists (it should be at top from initial load)
      const contactIdToRemove = selectedContactId;
      for (const group of groups) {
        const originalLength = group.items.length;
        group.items = group.items.filter((contact: any) => {
          const contactId = contact.zoho_customerid || contact.zoho_vendorid;
          return contactId !== contactIdToRemove;
        });
        // If we removed a duplicate, log it for debugging
        if (group.items.length < originalLength) {
          console.log(
            `Removed duplicate selected contact from ${group.groupLabel} during pagination`,
          );
        }
      }
    }

    const result = {
      items: groups,
      hasMore: overallHasMore,
    };

    // Cache only initial loads
    if (shouldCache) {
      setCached("contacts", cacheKey, result);
    }

    return result;
  };

  // Helper to get contact display name
  const getContactDisplayName = (contactId: string, item: any): string => {
    if (!contactId) return "Select contact";

    // Use contact_name from item if available
    if (item.contact_name) return item.contact_name;

    // Check cache
    const cached = contactsCache.get(contactId);
    if (cached) {
      return (
        cached.customer_name || cached.vendor_legal_name || "Select contact"
      );
    }

    return "Select contact";
  };

  // AP/AR journal: fetch bills for selected vendor (Accounts Payable line)
  const fetchBillsForContact =
    (vendorZohoId: string) =>
    async (
      searchTerm: string,
      limit: number,
      offset: number,
    ): Promise<{
      items: Array<{
        bill_id: string;
        bill_number?: string;
        total: number;
        balance: number;
        currency_code: string;
      }>;
      hasMore?: boolean;
    }> => {
      if (!vendorZohoId?.trim()) return { items: [], hasMore: false };
      const res = await getBillsForVendor(
        vendorZohoId.trim(),
        limit,
        offset,
        searchTerm || undefined,
      );
      const hasMore =
        (res as { pagination?: { has_more?: boolean } }).pagination?.has_more ??
        false;
      return { items: res.data ?? [], hasMore };
    };

  // AP/AR journal: fetch invoices for selected customer (Accounts Receivable line)
  const fetchInvoicesForContact =
    (customerZohoId: string) =>
    async (
      searchTerm: string,
      limit: number,
      offset: number,
    ): Promise<{
      items: Array<{
        invoice_id: string;
        invoice_number: string;
        total: number;
        balance: number;
        currency_code?: string;
      }>;
      hasMore?: boolean;
    }> => {
      if (!customerZohoId?.trim()) return { items: [], hasMore: false };
      const res = await getInvoicesForCustomer(
        customerZohoId.trim(),
        limit,
        offset,
        searchTerm || undefined,
      );
      const hasMore =
        (res as { pagination?: { has_more?: boolean } }).pagination?.has_more ??
        false;
      return { items: res.data ?? [], hasMore };
    };

  // AP/AR journal: handle Bill or Invoice selection (optional reference per line)
  const handleReferenceChange = (
    lineItemId: string,
    item: {
      bill_id?: string;
      invoice_id?: string;
      bill_number?: string;
      invoice_number?: string;
      total: number;
      balance?: number;
      currency_code?: string;
      status?: string;
    },
  ) => {
    const id = item.bill_id ?? item.invoice_id ?? "";
    const bal = Number(item.balance ?? item.total);
    const label = item.bill_id
      ? `Bill #${item.bill_number || id} - ${item.currency_code || ""} ${Number(bal).toLocaleString()}`
      : `Invoice #${item.invoice_number || id} - ${item.currency_code || ""} ${Number(bal).toLocaleString()}`;
    setLineItems((prev) =>
      prev.map((line) =>
        line.id === lineItemId
          ? {
              ...line,
              reference_id: id,
              reference_display: label,
              reference_balance: bal,
              reference_status: item.status ?? undefined,
            }
          : line,
      ),
    );
  };

  // Helper function to extract meaningful error message
  // Helper function to check if a date is the first day of a month
  const isFirstDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getDate() === 1;
  };

  // Helper function to check if a date is the last day of a month
  const isLastDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getDate() === 1;
  };

  // Helper function to check if a date is end of quarter (Mar 31, Jun 30, Sep 30, Dec 31)
  const isEndOfQuarter = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();

    return (
      (month === 2 && day === 31) || // March 31
      (month === 5 && day === 30) || // June 30
      (month === 8 && day === 30) || // September 30
      (month === 11 && day === 31) // December 31
    );
  };

  // Helper function to check if a date is end of year (Dec 31)
  const isEndOfYear = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getMonth() === 11 && date.getDate() === 31;
  };

  // Helper function to validate start date based on frequency
  const isValidStartDate = (dateStr: string, frequency: string): boolean => {
    if (!dateStr || !frequency) return true; // Don't validate if empty

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateStr);
    selectedDate.setHours(0, 0, 0, 0);

    switch (frequency) {
      case "start_of_month":
        return isFirstDayOfMonth(dateStr) && selectedDate > today;

      case "end_of_month":
        return isLastDayOfMonth(dateStr) && selectedDate > today;

      case "every_n_days":
        if (!recurringInterval) return true;
        const minDate = new Date(today);
        minDate.setDate(minDate.getDate() + Number(recurringInterval));
        return selectedDate >= minDate;

      case "end_of_year":
        return isEndOfYear(dateStr) && selectedDate > today;

      case "end_of_quarter":
        return isEndOfQuarter(dateStr) && selectedDate > today;

      case "date_of_month":
        return (
          !isFirstDayOfMonth(dateStr) &&
          !isLastDayOfMonth(dateStr) &&
          selectedDate > today
        );

      default:
        return selectedDate >= today;
    }
  };

  // Helper function to get minimum date based on frequency
  const getMinStartDate = (frequency: string): string => {
    const today = new Date();

    switch (frequency) {
      case "every_n_days":
        if (recurringInterval) {
          const minDate = new Date(today);
          minDate.setDate(minDate.getDate() + Number(recurringInterval));
          return minDate.toISOString().split("T")[0];
        }
        return today.toISOString().split("T")[0];

      default:
        // For all other frequencies, tomorrow is the minimum
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    }
  };

  // Helper function to get help text for start date based on frequency
  const getStartDateHelpText = (frequency: string): string => {
    switch (frequency) {
      case "start_of_month":
        return "⚠️ You must select the 1st day of a month";
      case "end_of_month":
        return "⚠️ You must select the last day of a month";
      case "end_of_quarter":
        return "⚠️ You must select a quarter-end date: Mar 31, Jun 30, Sep 30, or Dec 31";
      case "end_of_year":
        return "⚠️ You must select December 31st";
      case "date_of_month":
        return "⚠️ You must select any date EXCEPT the 1st or last day of the month";
      case "every_n_days":
        return recurringInterval
          ? `⚠️ Start date must be at least ${recurringInterval} days from today`
          : "Select number of days first";
      default:
        return "";
    }
  };

  // Initialize date to today
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  // Fetch currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
        // Set default currency to INR
        const inrCurrency = response.data.find(
          (c) => c.currency_code === "INR",
        );
        if (inrCurrency) {
          setCurrencyId(inrCurrency.currency_id);
          setCurrencySymbol(inrCurrency.currency_symbol);
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Reset line items when journal mode changes; force INR when in AP/AR mode
  const prevJournalModeRef = useRef(journalMode);
  useEffect(() => {
    const modeChanged = prevJournalModeRef.current !== journalMode;
    if (modeChanged) {
      prevJournalModeRef.current = journalMode;
      setLineItems((prev) =>
        prev.map((item) => ({
          id: item.id,
          account_type: "",
          account_id: "",
          account_name: "",
          description: "",
          contact_id: "",
          contact_name: "",
          customer_or_vendor: "",
          reference_id: "",
          reference_display: "",
          debit: "",
          credit: "",
          exchange_rate: "",
        })),
      );
    }
    if (journalMode === "ap_ar" && currencies.length > 0) {
      const inrCurrency = currencies.find((c) => c.currency_code === "INR");
      if (inrCurrency) {
        setCurrencyId(inrCurrency.currency_id);
        setCurrencySymbol(
          inrCurrency.currency_symbol ?? inrCurrency.currency_code,
        );
      }
    }
  }, [journalMode, currencies]);

  // Note: We no longer fetch all customers/vendors on mount
  // They are now fetched on-demand via SearchableDropdown

  const handleJournalAccountSelect = (
    lineItemId: string,
    account: GLMappingEntity,
  ) => {
    const currentItem = lineItems.find((item) => item.id === lineItemId);
    const previousAccountType = currentItem?.account_type ?? "";
    const nextAccountType = account.account_type ?? previousAccountType;
    const accountTypeChanged =
      previousAccountType !== "" &&
      nextAccountType !== "" &&
      previousAccountType !== nextAccountType;

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return {
          ...item,
          account_id: account.account_id.toString(),
          account_name: account.gl_name || "",
          account_type: nextAccountType,
          ...(accountTypeChanged
            ? {
                reference_id: "",
                reference_display: "",
                reference_balance: undefined,
                reference_status: undefined,
                contact_id: "",
                contact_name: "",
                customer_or_vendor: "" as const,
                debit: "",
                credit: "",
                exchange_rate: "",
              }
            : {}),
        };
      }),
    );

    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_account`);
      if (accountTypeChanged) {
        clearFieldError(`lineItem_${index}_contact`);
        clearFieldError(`lineItem_${index}_exchange_rate`);
      }
    }
  };

  // Handle contact selection
  const handleContactChange = (lineItemId: string, contact: any) => {
    const contactId = contact.zoho_customerid || contact.zoho_vendorid;

    // Get account name before updating (needed for caching)
    const currentItem = lineItems.find((item) => item.id === lineItemId);
    const accountName = currentItem?.account_name || "";

    // Cache the contact for future reference
    setContactsCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(contactId, contact);
      return newCache;
    });

    // Cache the selected contact in the dropdown cache so it's always available
    // This ensures when user reopens dropdown, selected contact is visible
    if (accountName) {
      const contactType = contact.zoho_customerid ? "customer" : "vendor";
      const cacheKey = {
        accountName,
        searchTerm: "",
        limit: 20,
        offset: 0,
        currencyId,
      };
      const cached = getCached<{
        items: GroupedItems<any>[];
        hasMore?: boolean;
      }>("contacts", cacheKey);

      if (cached) {
        // Check if contact is already in cache
        let hasContact = false;
        for (const group of cached.items) {
          hasContact = group.items.some((c: any) => {
            const cId = c.zoho_customerid || c.zoho_vendorid;
            return cId === contactId;
          });
          if (hasContact) break;
        }

        if (!hasContact) {
          // Add selected contact to appropriate group
          const groupLabel =
            contactType === "customer" ? "Customers" : "Vendors";
          let targetGroup = cached.items.find(
            (g: GroupedItems<any>) => g.groupLabel === groupLabel,
          );

          if (!targetGroup) {
            targetGroup = { groupLabel, items: [], hasMore: false };
            cached.items.push(targetGroup);
          }

          // Prepend selected contact to the group
          targetGroup.items = [contact, ...targetGroup.items];

          setCached("contacts", cacheKey, cached);
        }
      }
    }

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          const updatedItem: JournalLineItemForm = {
            ...item,
            contact_id: contactId,
            contact_name:
              contact.customer_name || contact.vendor_legal_name || "",
            customer_or_vendor: contact.zoho_customerid
              ? ("customer" as const)
              : contact.zoho_vendorid
                ? ("vendor" as const)
                : "",
            reference_id: "",
            reference_display: "",
          };

          return updatedItem;
        }
        return item;
      }),
    );

    // Clear errors for this line item
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_contact`);
    }
  };

  // Clear contact (and bill/invoice reference) for a line item - for both manual and AP/AR journals
  const handleClearContact = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          return {
            ...item,
            contact_id: "",
            contact_name: "",
            customer_or_vendor: "" as const,
            reference_id: "",
            reference_display: "",
            reference_balance: undefined,
            reference_status: undefined,
            exchange_rate: "",
          };
        }
        return item;
      }),
    );
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_contact`);
      clearFieldError(`lineItem_${index}_exchange_rate`);
    }
  };

  // Reject negative values for debit/credit (typing and paste)
  const allowOnlyNonNegative = (value: string): boolean =>
    value === "" ||
    (!value.includes("-") &&
      (Number.isNaN(parseFloat(value)) || parseFloat(value) >= 0));

  // Handle line item field change
  const handleLineItemChange = (
    lineItemId: string,
    field: keyof JournalLineItemForm,
    value: string,
  ) => {
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    const isDescriptionOverLimit =
      isApArJournal &&
      field === "description" &&
      value.length > AP_AR_DESCRIPTION_MAX_LENGTH;
    const nextValue = isDescriptionOverLimit
      ? value.slice(0, AP_AR_DESCRIPTION_MAX_LENGTH)
      : value;

    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, [field]: nextValue } : item,
      ),
    );

    // Clear errors for this line item field
    if (index !== -1) {
      // Clear specific field error and amount error (since debit/credit affect it)
      if (isDescriptionOverLimit) {
        setFieldError(
          `lineItem_${index}_description`,
          `Description cannot exceed ${AP_AR_DESCRIPTION_MAX_LENGTH} characters`,
        );
      } else {
        clearFieldError(`lineItem_${index}_${field}`);
      }
      if (field === "debit" || field === "credit") {
        clearFieldError(`lineItem_${index}_amount`);
        clearFieldError("balance"); // Also clear balance error when amounts change
      }
      if (field === "exchange_rate") {
        clearFieldError(`lineItem_${index}_exchange_rate`);
      }
    }
  };

  // Add new line item
  const handleAddRow = () => {
    const newId = (
      Math.max(...lineItems.map((item) => parseInt(item.id))) + 1
    ).toString();
    setLineItems((prev) => [
      ...prev,
      {
        id: newId,
        account_type: "",
        account_id: "",
        account_name: "",
        description: "",
        contact_id: "",
        contact_name: "",
        customer_or_vendor: "",
        reference_id: "",
        reference_display: "",
        debit: "",
        credit: "",
        exchange_rate: "",
      },
    ]);
  };

  // Remove line item
  const handleRemoveRow = (lineItemId: string) => {
    if (lineItems.length > 2) {
      setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
    } else {
      showError("You must have at least 2 line items");
    }
  };

  // Effective amount in journal currency (for non-INR: entered amount × exchange_rate)
  const getEffectiveAmountInJournalCurrency = (
    item: JournalLineItemForm,
  ): number => {
    const debit = parseFloat(item.debit) || 0;
    const credit = parseFloat(item.credit) || 0;
    const raw = debit > 0 ? debit : credit;
    if (raw <= 0) return 0;
    const needsRate =
      isApArJournal &&
      (item.account_type === "accounts_payable" ||
        item.account_type === "accounts_receivable") &&
      item.contact_id &&
      isContactCurrencyDifferentFromJournal(item);
    const rate = parseFloat(item.exchange_rate ?? "") || 0;
    if (needsRate && rate > 0) return Math.round(raw * rate * 100) / 100;
    return raw;
  };

  // Calculate totals (in journal currency; non-INR lines use amount × exchange_rate)
  const getTotalDebits = (): number => {
    return lineItems.reduce((sum, item) => {
      const debit = parseFloat(item.debit) || 0;
      if (debit <= 0) return sum;
      return sum + getEffectiveAmountInJournalCurrency(item);
    }, 0);
  };

  const getTotalCredits = (): number => {
    return lineItems.reduce((sum, item) => {
      const credit = parseFloat(item.credit) || 0;
      if (credit <= 0) return sum;
      return sum + getEffectiveAmountInJournalCurrency(item);
    }, 0);
  };

  const getDifference = (): number => {
    return getTotalDebits() - getTotalCredits();
  };

  // Helper function to prepare journal data for API (reusable for review requests)
  const prepareJournalData = () => {
    const apiLineItems: any[] = lineItems.map((item) => {
      const debit = parseFloat(item.debit) || 0;
      const amountInJournalCurrency = getEffectiveAmountInJournalCurrency(item);
      const isVendor = item.customer_or_vendor === "vendor";
      const lineItem: any = {
        account_id: item.account_id,
        is_debit_or_credit: debit > 0 ? "debit" : "credit",
        amount: amountInJournalCurrency,
        description: item.description,
        contact_id: item.contact_id || undefined,
        customer_or_vendor: item.customer_or_vendor || undefined,
        invoice_id:
          !isVendor && item.reference_id ? item.reference_id : undefined,
        bill_id: isVendor && item.reference_id ? item.reference_id : undefined,
        document_status: item.reference_status ?? undefined,
        document_balance:
          item.reference_balance != null ? item.reference_balance : undefined,
      };

      // Include account_name and contact_name in raw_json for better display
      if (item.account_name) {
        lineItem.account_name = item.account_name;
      }
      if (item.contact_name) {
        lineItem.contact_name = item.contact_name;
      }
      if (item.reference_display?.trim() && item.reference_id) {
        lineItem.reference_display = item.reference_display.trim();
      }

      const needsExchangeRate =
        isApArJournal &&
        (item.account_type === "accounts_payable" ||
          item.account_type === "accounts_receivable") &&
        item.contact_id &&
        isContactCurrencyDifferentFromJournal(item);
      if (needsExchangeRate && item.exchange_rate) {
        const rate = parseFloat(item.exchange_rate);
        if (!Number.isNaN(rate) && rate > 0) {
          lineItem.exchange_rate = rate;
          const contactSymbol = getContactCurrencyCodeForLineItem(item);
          if (contactSymbol) {
            lineItem.contact_currency_symbol = contactSymbol;
            lineItem.contact_currency_id =
              getContactCurrencyIdForLineItem(item);
          }
        }
      }

      return lineItem;
    });

    return {
      journal: {
        date,
        reference_number: referenceNumber,
        currency_id: currencyId,
        currency_symbol: currencySymbol,
        notes,
        type: reportingMethod,
        // ZOUR-65 F4: the registration this journal is filed under. Sent on
        // every path this function feeds — draft, publish and review — so the
        // three cannot drift. The backend folds the primary's own id back to
        // NULL (F1 `foldPrimaryLocation`), so sending it explicitly is safe.
        location_id: locationId,
      },
      line_items: apiLineItems,
      is_recurring: isRecurring === "yes",
      ...(isRecurring === "yes" && {
        recurrence_frequency: recurringFrequency,
        template_name: recurringJournalName,
        starts_on: recurringStartDate,
        ...(recurringFrequency === "every_n_days" && {
          recurrence_interval: recurringInterval,
        }),
        ...(recurringNeverExpires
          ? { never_expires: true }
          : { ends_on: recurringEndDate, never_expires: false }),
      }),
    };
  };

  // Helper function to calculate field_changes for review request
  // For new journals, we don't have old values, so we'll only include basic info
  const calculateFieldChanges = (): Array<{
    field: string;
    old: string;
    new: string;
  }> => {
    const changes: Array<{ field: string; old: string; new: string }> = [];

    // For new journals, we can show what will be created
    if (date) changes.push({ field: "Date", old: "", new: date });
    if (referenceNumber)
      changes.push({
        field: "Reference Number",
        old: "",
        new: referenceNumber,
      });
    if (currencySymbol)
      changes.push({ field: "Currency", old: "", new: currencySymbol });
    if (notes) changes.push({ field: "Notes", old: "", new: notes });
    if (reportingMethod)
      changes.push({
        field: "Reporting Method",
        old: "",
        new: reportingMethod,
      });

    // Add recurring journal fields if applicable
    if (isRecurring === "yes") {
      changes.push({
        field: "is_recurring",
        old: "",
        new: "yes",
      });
      if (recurringJournalName)
        changes.push({
          field: "template_name",
          old: "",
          new: recurringJournalName,
        });
      if (recurringFrequency)
        changes.push({
          field: "recurrence_frequency",
          old: "",
          new: recurringFrequency,
        });
      if (recurringInterval)
        changes.push({
          field: "recurrence_interval",
          old: "",
          new: String(recurringInterval),
        });
      if (recurringStartDate)
        changes.push({
          field: "starts_on",
          old: "",
          new: recurringStartDate,
        });
      if (recurringNeverExpires)
        changes.push({
          field: "never_expires",
          old: "",
          new: "yes",
        });
      if (!recurringNeverExpires && recurringEndDate)
        changes.push({
          field: "ends_on",
          old: "",
          new: recurringEndDate,
        });
    }

    // Add line items
    lineItems.forEach((item, index) => {
      if (item.account_name)
        changes.push({
          field: `line_items[${index}].account_name`,
          old: "",
          new: item.account_name,
        });
      if (item.contact_name)
        changes.push({
          field: `line_items[${index}].contact_name`,
          old: "",
          new: item.contact_name,
        });
      if (item.description)
        changes.push({
          field: `line_items[${index}].description`,
          old: "",
          new: item.description,
        });
      if (item.debit)
        changes.push({
          field: `line_items[${index}].debit`,
          old: "",
          new: item.debit,
        });
      if (item.credit)
        changes.push({
          field: `line_items[${index}].credit`,
          old: "",
          new: item.credit,
        });
      const needsExchangeRate =
        isApArJournal &&
        (item.account_type === "accounts_payable" ||
          item.account_type === "accounts_receivable") &&
        item.contact_id &&
        isContactCurrencyDifferentFromJournal(item);
      if (needsExchangeRate && item.exchange_rate)
        changes.push({
          field: `line_items[${index}].exchange_rate`,
          old: "",
          new: item.exchange_rate,
        });
    });

    return changes;
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!date) newErrors.date = "Date is required";
    // Journal number is not required for drafts (auto-generated on publish)
    // if (!journalNumber) newErrors.journalNumber = "Journal number is required";
    if (!notes || notes.trim() === "") newErrors.notes = "Notes are required";

    // Validate recurring journal fields
    if (isRecurring === "yes") {
      if (!recurringStartDate)
        newErrors.recurringStartDate =
          "Start date is required for recurring journals";
      if (!recurringFrequency)
        newErrors.recurringFrequency =
          "Frequency is required for recurring journals";
      if (recurringFrequency === "every_n_days" && !recurringInterval)
        newErrors.recurringInterval = "Number of days is required";

      // Validate start date based on frequency
      if (recurringStartDate && recurringFrequency) {
        if (!isValidStartDate(recurringStartDate, recurringFrequency)) {
          switch (recurringFrequency) {
            case "start_of_month":
              newErrors.recurringStartDate =
                "Start date must be the 1st of a month after today";
              break;
            case "end_of_month":
              newErrors.recurringStartDate =
                "Start date must be the last day of a month after today";
              break;
            case "every_n_days":
              newErrors.recurringStartDate = `Start date must be at least ${recurringInterval} days from today`;
              break;
            case "end_of_year":
              newErrors.recurringStartDate = "Start date must be December 31st";
              break;
            case "end_of_quarter":
              newErrors.recurringStartDate =
                "Start date must be the end of a quarter (Mar 31, Jun 30, Sep 30, or Dec 31)";
              break;
            case "date_of_month":
              newErrors.recurringStartDate =
                "Start date cannot be the 1st or last day of the month";
              break;
          }
        }
      }
    }
    if (notes.length > 500)
      newErrors.notes = "Notes cannot exceed 500 characters";

    // Validate line items
    lineItems.forEach((item, index) => {
      if (!item.account_id)
        newErrors[`lineItem_${index}_account`] = "Account is required";
      // AP/AR journal: description is required per line
      if (
        isApArJournal &&
        (!item.description || item.description.trim() === "")
      ) {
        newErrors[`lineItem_${index}_description`] = "Description is required";
      } else if (
        isApArJournal &&
        item.description.length > AP_AR_DESCRIPTION_MAX_LENGTH
      ) {
        newErrors[`lineItem_${index}_description`] =
          `Description cannot exceed ${AP_AR_DESCRIPTION_MAX_LENGTH} characters`;
      }

      // Validate contact only for Accounts Payable and Accounts Receivable
      if (isContactRequired(item.account_name) && !item.contact_id) {
        newErrors[`lineItem_${index}_contact`] =
          "Contact is required for this account type";
      }

      const debit = parseFloat(item.debit) || 0;
      const credit = parseFloat(item.credit) || 0;

      if (debit === 0 && credit === 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Either debit or credit must be specified";
      }

      if (debit > 0 && credit > 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Cannot have both debit and credit in the same line";
      }

      // AP/AR: when contact currency differs from journal currency, exchange rate is required
      const needsExchangeRate =
        isApArJournal &&
        (item.account_type === "accounts_payable" ||
          item.account_type === "accounts_receivable") &&
        item.contact_id &&
        isContactCurrencyDifferentFromJournal(item);
      if (needsExchangeRate) {
        const rate = parseFloat(item.exchange_rate ?? "");
        if (Number.isNaN(rate) || rate <= 0) {
          newErrors[`lineItem_${index}_exchange_rate`] =
            "Exchange rate is required for this contact (different currency)";
        }
      }
    });

    // AP/AR journal: at least one line item must be Accounts Payable or Accounts Receivable
    if (isApArJournal) {
      const hasAtLeastOneApAr = lineItems.some(
        (item) =>
          isApAccountType(item.account_type || "") ||
          isArAccountType(item.account_type || ""),
      );
      if (!hasAtLeastOneApAr) {
        newErrors.apArLineItems =
          "At least one line item must be Accounts Payable or Accounts Receivable.";
      }

      // When Bad Debt is selected: exactly 2 line items, one must be AR, and AR must have an invoice
      const hasBadDebt = lineItems.some(
        (item) =>
          !!item.account_id &&
          (item.account_type === "expense" ||
            (item.account_name?.split(">").pop()?.trim() === "Bad Debt" &&
              isBadDebtAccountTypeCandidate(item.account_type || ""))),
      );
      if (hasBadDebt) {
        const filledLines = lineItems.filter(
          (item) =>
            item.account_id &&
            (parseFloat(item.debit) > 0 || parseFloat(item.credit) > 0),
        );
        if (filledLines.length !== 2) {
          newErrors.apArLineItems =
            (newErrors.apArLineItems ? newErrors.apArLineItems + " " : "") +
            "When Bad Debt is selected, there must be exactly two line items.";
        } else {
          const arLine = filledLines.find((item) =>
            isArAccountType(item.account_type || ""),
          );
          if (!arLine) {
            newErrors.apArLineItems =
              (newErrors.apArLineItems ? newErrors.apArLineItems + " " : "") +
              "When Bad Debt is selected, one line item must be Accounts Receivable.";
          } else if (!arLine.reference_id) {
            newErrors.apArLineItems =
              (newErrors.apArLineItems ? newErrors.apArLineItems + " " : "") +
              "When Bad Debt is selected, an invoice must be selected for the AR contact.";
          }
        }
      }
    }

    // Check if debits equal credits
    const difference = getDifference();
    if (Math.abs(difference) > 0.01) {
      newErrors.balance = "Total debits must equal total credits";
    }

    // AP/AR: if a line has a bill/invoice selected, amount must not exceed its balance
    const referenceBalanceViolations: string[] = [];
    lineItems.forEach((item, index) => {
      const bal = item.reference_balance;
      if (item.reference_id && bal != null && bal > 0) {
        const amount = parseFloat(item.debit) || parseFloat(item.credit) || 0;
        if (amount > bal) {
          const lineNum = index + 1;
          const refLabel =
            item.account_type === "accounts_payable" ? "bill" : "invoice";
          referenceBalanceViolations.push(
            `Line ${lineNum}: amount exceeds ${refLabel} balance (balance: ${bal.toLocaleString()})`,
          );
        }
      }
    });
    if (referenceBalanceViolations.length > 0) {
      newErrors.referenceBalance = referenceBalanceViolations.join(". ");
    }

    // Set errors using new error handling hook
    console.log("Validation errors found:", newErrors);
    setFieldErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log("Form is valid:", isValid);
    return isValid;
  };

  // Handle save as draft
  const handleSaveAsDraft = async () => {
    if (!validateForm()) {
      // ErrorBanner will display validation errors and auto-scroll
      return;
    }

    // Closed month: non-Admin/CFO must submit via review
    // if (isMonthClosed === true && !isAdminOrCFO) {
    //   setPendingClosedMonthAction("save_as_draft");
    //   setShowClosedMonthConfirmModal(true);
    //   return;
    // }

    // Validate recurring journal end date
    if (isRecurring === "yes" && !recurringNeverExpires && !recurringEndDate) {
      setFieldError(
        "recurringEndDate",
        "Please either enable 'Never expires' or select an end date for the recurring journal",
      );
      return;
    }

    // When saving as draft + recurring, send review request if create_recurring requires review
    const needsReviewForRecurringDraft =
      isRecurring === "yes" && needsReviewForCreateRecurring;
    if (needsReviewForRecurringDraft) {
      const journalData = prepareJournalData();
      const fieldChanges = calculateFieldChanges();
      setReviewChangeDetails({
        raw_json: journalData,
        field_changes: fieldChanges,
        action_types: [ACTION_CODES.CREATE_RECURRING],
      });
      setReviewActionCode(ACTION_CODES.CREATE_RECURRING);
      setPendingAction("save_as_draft");
      setIsReviewModalOpen(true);
      return;
    }

    // Proceed with save (no review needed, use save_as_draft status)
    await executeSaveAsDraft("save_as_draft");
  };

  // Create AP/AR journal (direct create or draft + submit for review)
  const handleCreateApArJournal = async () => {
    if (!validateForm()) return;
    try {
      setIsLoading(true);
      const journalType: "cash" | "both" =
        reportingMethod === "cash" ? "cash" : "both";
      const journalData = prepareJournalData();
      const payload = {
        journal: {
          ...journalData.journal,
          type: journalType,
        },
        line_items: journalData.line_items.filter(
          (li: any) => li.account_id && (li.amount ?? 0) > 0,
        ),
      };
      if (payload.line_items.length === 0) {
        showError("Add at least one line with account and amount");
        return;
      }

      if (needsReviewForApArCreate) {
        setReviewChangeDetails({
          action_types: ["ap_ar_create"],
          raw_json: payload,
        });
        setReviewActionCode(ACTION_CODES.AP_AR_CREATE);
        setIsReviewModalOpen(true);
        return;
      } else {
        await journalsAPI.createApArJournal(payload);
        showSuccess("AP/AR journal created");
        navigate("/dashboard/journal");
      }
    } catch (err: any) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Execute save as draft (called after review approval or if no review needed)
  const executeSaveAsDraft = async (
    status: "save_as_draft" | "under_review",
  ) => {
    try {
      setIsLoading(true);

      const journalData = prepareJournalData();
      console.log("Creating draft journal:", journalData);

      // Call API with attachments and status
      const response = await journalsAPI.createDraftJournal(
        journalData,
        attachments,
        status,
      );

      // If journal was created, store the ID for review banner
      if (response.data?.id) {
        setCreatedJournalId(
          typeof response.data.id === "string"
            ? parseInt(response.data.id, 10)
            : response.data.id,
        );
      }
      if (status === "under_review") {
        showSuccess("Journal draft created successfully and review requested");
      } else {
        showSuccess("Journal draft created successfully");
      }
      navigate("/dashboard/journal");
    } catch (error: any) {
      console.log("Error creating journal draft:", error);

      // Use centralized error parsing and display
      setApiErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save and publish (create and post to Zoho)
  const handleSaveAndPublish = async () => {
    if (!validateForm()) {
      // ErrorBanner will display validation errors and auto-scroll
      return;
    }

    // Closed month: non-Admin/CFO must submit via review
    if (isMonthClosed === true && !isAdminOrCFO) {
      setPendingClosedMonthAction("save_and_publish");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // Validate recurring journal end date
    if (isRecurring === "yes" && !recurringNeverExpires && !recurringEndDate) {
      setFieldError(
        "recurringEndDate",
        "Please either enable 'Never expires' or select an end date for the recurring journal",
      );
      return;
    }

    // Check if attachments are provided
    const hasAttachments = attachments.length > 0;

    // Check review requirements with precedence order
    // PRIORITY 1: Check if basic push to Zoho needs review (highest priority)
    const needsReviewForPublish = needsReviewForPushToZoho;

    // PRIORITY 2: Check if push without attachment needs review
    // Only applicable when:
    // - Basic push doesn't require review
    // - No attachments provided
    // - User has permission to push
    // - This specific action requires review
    const needsReviewForPushWithoutAttachment =
      !needsReviewForPublish &&
      !hasAttachments &&
      canPushToZoho &&
      needsReviewForPushToZohoWithoutAttachment;

    // PRIORITY 3: Check recurring journal review
    // needsReviewForRecurring already includes the isRecurring === "yes" check
    const needsReviewForRecurring =
      isRecurring === "yes" && needsReviewForCreateRecurring;

    // Determine which reviews are needed
    const needsPublishReview = needsReviewForPublish;
    const needsAttachmentReview = needsReviewForPushWithoutAttachment;
    const needsRecurringReview = needsReviewForRecurring;

    if (needsPublishReview || needsAttachmentReview || needsRecurringReview) {
      // Determine primary action code and action_types array
      let primaryActionCode: string;
      const actionTypes: string[] = [];

      // Priority order determines which action is primary
      if (needsPublishReview) {
        // Basic push review takes precedence
        primaryActionCode = ACTION_CODES.PUSH_TO_ZOHO;
        actionTypes.push(ACTION_CODES.PUSH_TO_ZOHO);

        // Only add recurring if it actually needs review
        if (needsRecurringReview) {
          actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        }
      } else if (needsAttachmentReview) {
        // Push without attachment review (only if basic push doesn't need review)
        primaryActionCode = ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT;
        actionTypes.push(ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT);

        // Only add recurring if it actually needs review
        if (needsRecurringReview) {
          actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        }
      } else if (needsRecurringReview) {
        // Only recurring needs review
        primaryActionCode = ACTION_CODES.CREATE_RECURRING;
        actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        // Need to do this !!
        actionTypes.push(ACTION_CODES.PUSH_TO_ZOHO);
      }

      // Show review modal
      const journalData = prepareJournalData();
      const fieldChanges = calculateFieldChanges();

      setReviewChangeDetails({
        raw_json: journalData,
        field_changes: fieldChanges,
        action_types: actionTypes.length > 0 ? actionTypes : undefined,
      });
      setReviewActionCode(primaryActionCode!);
      setPendingAction("save_and_publish");
      setIsReviewModalOpen(true);
      return;
    }

    // Proceed with publish
    await executeSaveAndPublish();
  };

  // Execute save and publish (called after review approval or if no review needed)
  const executeSaveAndPublish = async () => {
    try {
      setIsLoading(true);

      const journalData = prepareJournalData();

      // Call API with attachments
      const response = await journalsAPI.postJournalToAccounting(
        journalData,
        attachments,
      );

      // If journal was created, store the ID for review banner
      if (response.data?.id) {
        setCreatedJournalId(
          typeof response.data.id === "string"
            ? parseInt(response.data.id, 10)
            : response.data.id,
        );
      }

      if (response.success && response.error_details) {
        showError(`${response.message}. ${response.error_details}`);
      } else if (response.success) {
        showSuccess(response.message || "Journal created successfully");
      }

      navigate("/dashboard/journal");
    } catch (error: any) {
      console.error("Error creating and publishing journal:", error);

      // Use centralized error parsing and display
      setApiErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/dashboard/journal");
  };

  // Handle review modal success
  const handleReviewModalSuccess = async (comments?: string) => {
    try {
      setIsLoading(true);

      // AP/AR create: create draft after user submits from the modal, then send the review request
      if (reviewActionCode === ACTION_CODES.AP_AR_CREATE) {
        const payload = reviewChangeDetails?.raw_json;
        if (!payload?.journal || !payload?.line_items) {
          throw new Error("AP/AR journal data not found");
        }
        const draftRes = await journalsAPI.createApArJournalDraft(payload);
        const draftId = draftRes.data?.id;
        if (draftId == null) {
          throw new Error("Failed to create AP/AR journal draft");
        }
        const draftIdNum =
          typeof draftId === "string" ? parseInt(draftId, 10) : draftId;
        const { createReviewRequest } =
          await import("../shared/utils/reviewWorkflow");
        await createReviewRequest({
          module_code: MODULE_CODES.AP_AR_JOURNAL,
          action_code: ACTION_CODES.AP_AR_CREATE,
          entity_type: "ap_ar_journal",
          entity_id: draftIdNum,
          request_comments: comments || "Submit AP/AR journal for approval",
          change_details: reviewChangeDetails || undefined,
        });
        setIsReviewModalOpen(false);
        setPendingAction(null);
        setReviewChangeDetails(null);
        setReviewActionCode("");
        showSuccess("AP/AR journal submitted for review");
        navigate("/dashboard/journal");
        return;
      }

      // First, save the journal as draft (without recurring params if it's recurring)
      // This ensures we have a real journal ID before sending the review request
      const journalData = prepareJournalData();

      // If it's a recurring journal, save without recurring parameters first
      let journalDataForDraft: any;
      if (isRecurring === "yes") {
        // Create a copy without recurring fields for initial draft save
        const {
          recurrence_frequency,
          template_name,
          starts_on,
          ends_on,
          never_expires,
          recurrence_interval,
          reference_number_prefix,
          ...rest
        } = journalData as any;
        journalDataForDraft = {
          ...rest,
          is_recurring: false,
        };
      } else {
        journalDataForDraft = journalData;
      }

      // Save journal as draft first to get the journal ID
      const response = await journalsAPI.createDraftJournal(
        journalDataForDraft,
        attachments,
        "save_as_draft", // Save as draft first, status will be updated to under_review by ReviewService
      );

      if (!response.data?.id) {
        throw new Error("Failed to create journal");
      }

      const journalId =
        typeof response.data.id === "string"
          ? parseInt(response.data.id, 10)
          : response.data.id;

      // Store the journal ID for review banner
      setCreatedJournalId(journalId);

      // Now send the review request with the actual journal ID
      // The ReviewService will update the status to under_review
      const { createReviewRequest } =
        await import("../shared/utils/reviewWorkflow");
      await createReviewRequest({
        module_code: MODULE_CODES.JOURNAL,
        action_code: reviewActionCode,
        entity_type: "journal",
        entity_id: journalId, // Use the actual journal ID
        request_comments: comments || "Please review this journal",
        change_details: reviewChangeDetails || undefined,
      });

      // Close modal after successful review request
      setIsReviewModalOpen(false);

      // Clear review-related state
      setPendingAction(null);
      setReviewChangeDetails(null);
      setReviewActionCode("");

      showSuccess("Journal created successfully and review requested");
      navigate("/dashboard/journal");
    } catch (error: any) {
      console.error("Error saving journal after review request:", error);
      setApiErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Check if total files exceed 5
    if (attachments.length + fileArray.length > 5) {
      showError("You can upload a maximum of 5 files");
      return;
    }

    // Check each file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    for (const file of fileArray) {
      if (file.size > maxSize) {
        showError(`File "${file.name}" exceeds 10MB limit`);
        return;
      }
    }

    setAttachments((prev) => [...prev, ...fileArray]);
  };

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-8 py-4">
          <IconButton icon={<FeatherArrowLeft />} onClick={handleCancel} />
          <span className="text-body font-body text-default-font">
            Back to Journals
          </span>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 px-8 py-4 overflow-y-auto overflow-x-visible pb-16">
          <div className="flex w-full flex-col items-start gap-4 px-1 py-1">
            <span className="text-heading-1 font-heading-1 text-default-font">
              New Journal
            </span>
            <div className="flex items-center gap-2">
              <span className="text-body font-body text-subtext-color">
                Type:
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="journalMode"
                  value="manual"
                  checked={journalMode === "manual"}
                  onChange={() => setJournalMode("manual")}
                  className="w-4 h-4"
                />
                <span className="text-body font-body text-default-font">
                  Manual Journal
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="journalMode"
                  value="ap_ar"
                  checked={journalMode === "ap_ar"}
                  onChange={() => setJournalMode("ap_ar")}
                  className="w-4 h-4"
                />
                <span className="text-body font-body text-default-font">
                  AP/AR Journal
                </span>
              </label>
            </div>
          </div>

          {/* AP/AR Suspense Account banner */}
          {isApArJournal && (
            <div className="w-full pb-2">
              <div className="flex w-full items-center gap-2 rounded-md border border-solid border-info-200 bg-info-50 px-4 py-3">
                <span className="text-body font-body text-info-700">
                  All AP/AR transactions will be routed through the Suspense
                  Account.
                </span>
              </div>
            </div>
          )}

          {/* Display validation errors with new ErrorBanner component */}
          <div className="w-full pb-2">
            <ErrorBanner
              errors={errors}
              title="Please fix the following errors:"
              autoScrollOnMount={true}
            />
          </div>

          {/* Closed month banner */}
          {isMonthClosed === true && (
            <div className="w-full pb-2">
              <ClosedMonthBanner />
            </div>
          )}

          {/* Review Status Banner */}
          {review && createdJournalId && (
            <div className="w-full">
              <ReviewStatusBanner
                review={review}
                moduleCode={MODULE_CODES.JOURNAL}
                currentUserId={user?.id ? parseInt(user.id, 10) : null}
                onResubmit={() => {
                  if (review.status === "rejected") {
                    setIsReviewModalOpen(true);
                  }
                }}
              />
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-6">
            <div className="w-full items-start gap-6 grid grid-cols-2">
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Date
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={errors.date}
                  error={!!errors.date}
                  icon={<FeatherCalendar />}
                >
                  <TextField.Input
                    type="date"
                    placeholder="Select date"
                    value={date}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setDate(event.target.value);
                      clearFieldError("date");
                    }}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Journal #
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={
                    status === "under_review"
                      ? "The journal number will be auto generated after Publishing"
                      : errors.journalNumber
                  }
                  error={!!errors.journalNumber}
                  disabled={true}
                >
                  <TextField.Input
                    placeholder={
                      status === "under_review" ? "Auto-generated" : ""
                    }
                    value={""}
                    readOnly
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Reference #
                </span>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText=""
                >
                  <TextField.Input
                    placeholder="Optional"
                    value={referenceNumber}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setReferenceNumber(event.target.value)
                    }
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Currency
                </span>
                <Select
                  label=""
                  placeholder={
                    currenciesLoading ? "Loading..." : "Select currency"
                  }
                  value={currencyId}
                  onValueChange={(value: string) => {
                    setCurrencyId(value);
                    const currency = currencies.find(
                      (c) => c.currency_id === value,
                    );
                    if (currency) {
                      setCurrencySymbol(currency.currency_code);
                    }

                    // Clear contacts from all line items when currency changes
                    // since contacts are filtered by currency
                    setLineItems((prev) =>
                      prev.map((item) => ({
                        ...item,
                        contact_id: "",
                        contact_name: "",
                        customer_or_vendor: "",
                        reference_id: "",
                        reference_display: "",
                        exchange_rate: "",
                      })),
                    );

                    // Clear contacts cache
                    setContactsCache(new Map());

                    // Clear contacts dropdown cache
                    clearCache("contacts");
                  }}
                  disabled={currenciesLoading || isApArJournal}
                >
                  {currencies.map((currency) => (
                    <Select.Item key={currency.id} value={currency.currency_id}>
                      {currency.currency_code} – {currency.currency_name}
                    </Select.Item>
                  ))}
                </Select>
                {isApArJournal && (
                  <span className="text-body font-body text-subtext-color">
                    Apply amount in INR only.
                  </span>
                )}
              </div>
              {/* ZOUR-65 F4: which GST registration this journal is filed
                  under. Hidden entirely on a single-registration company —
                  there is nothing to choose. */}
              {hasMultipleLocations && (
                <div className="flex flex-col items-start gap-2">
                  <LocationPicker
                    locations={activeLocations}
                    value={locationId}
                    onChange={(picked) => setLocationId(picked)}
                  />
                </div>
              )}
              <div className="flex flex-col items-start gap-2 col-span-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Notes
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={
                    errors.notes || `Max. 500 characters · ${notes.length}/500`
                  }
                  error={!!errors.notes}
                >
                  <TextField.Input
                    placeholder="Enter notes"
                    value={notes}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNotes(event.target.value);
                      clearFieldError("notes");
                    }}
                    maxLength={500}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2 col-span-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Reporting Method
                </span>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="both"
                      checked={reportingMethod === "both"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-body font-body text-default-font">
                      Accrual and Cash
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="cash"
                      checked={reportingMethod === "cash"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-body font-body text-default-font">
                      Cash Only
                    </span>
                  </label>
                </div>
              </div>
              {!isApArJournal && canCreateRecurring && (
                <div className="flex flex-col items-start gap-2 col-span-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Recurring Journal
                  </span>
                  {/* <RadioCardGroup
                    className="h-auto w-full flex-none"
                    value={isRecurring}
                    onValueChange={(value: string) => setIsRecurring(value)}
                  >
                    <div className="flex items-start gap-4">
                      <RadioCardGroup.RadioCard value="yes">
                        <span className="text-body font-body text-default-font">
                          Yes
                        </span>
                      </RadioCardGroup.RadioCard>
                      <RadioCardGroup.RadioCard value="no">
                        <span className="text-body font-body text-default-font">
                          No
                        </span>
                      </RadioCardGroup.RadioCard>
                    </div>
                  </RadioCardGroup> */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="yes"
                        checked={isRecurring === "yes"}
                        onChange={(e) => setIsRecurring(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-body font-body text-default-font">
                        Yes
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="no"
                        checked={isRecurring === "no"}
                        onChange={(e) => setIsRecurring(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-body font-body text-default-font">
                        No
                      </span>
                    </label>
                  </div>
                </div>
              )}
              {isRecurring === "yes" && (
                <div className="flex flex-col items-start gap-4 border-l-2 border-solid border-neutral-200 pl-4 col-span-2">
                  <div className="w-full items-start gap-6 grid grid-cols-2">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Recuring Journal Name
                      </span>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText=""
                      >
                        <TextField.Input
                          placeholder="Enter recurring journal name"
                          value={recurringJournalName}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => setRecurringJournalName(event.target.value)}
                        />
                      </TextField>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Repeat Every
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          *
                        </span>
                      </div>
                      <Select
                        label=""
                        placeholder="Select frequency"
                        value={recurringFrequency}
                        onValueChange={(value: string) => {
                          setRecurringFrequency(value);
                          clearFieldError("recurringFrequency");
                        }}
                        error={!!errors.recurringFrequency}
                        helpText={errors.recurringFrequency}
                      >
                        <Select.Item value="daily">Daily</Select.Item>
                        <Select.Item value="start_of_month">
                          Start of Month
                        </Select.Item>
                        <Select.Item value="end_of_month">
                          End of Month
                        </Select.Item>
                        <Select.Item value="end_of_quarter">
                          End of Quarter
                        </Select.Item>
                        <Select.Item value="end_of_year">
                          End of Year
                        </Select.Item>
                        <Select.Item value="date_of_month">
                          Date of Month
                        </Select.Item>
                        <Select.Item value="every_n_days">
                          Every N Days
                        </Select.Item>
                      </Select>
                    </div>
                    {recurringFrequency === "every_n_days" && (
                      <div className="flex flex-col items-start gap-2">
                        <div className="flex items-center gap-1">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Number of Days
                          </span>
                          <span className="text-body-bold font-body-bold text-error-600">
                            *
                          </span>
                        </div>
                        <TextField
                          className="h-auto w-full flex-none"
                          label=""
                          helpText={errors.recurringInterval}
                          error={!!errors.recurringInterval}
                        >
                          <TextField.Input
                            type="number"
                            placeholder="Enter number of days"
                            value={recurringInterval.toString()}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setRecurringInterval(
                                event.target.value
                                  ? parseInt(event.target.value)
                                  : "",
                              );
                              clearFieldError("recurringInterval");
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="1"
                          />
                        </TextField>
                      </div>
                    )}
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Starts On
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          *
                        </span>
                      </div>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText={
                          errors.recurringStartDate ||
                          getStartDateHelpText(recurringFrequency)
                        }
                        error={!!errors.recurringStartDate}
                        icon={<FeatherCalendar />}
                      >
                        <TextField.Input
                          type="date"
                          placeholder="Select start date"
                          value={recurringStartDate}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setRecurringStartDate(event.target.value);
                            clearFieldError("recurringStartDate");
                          }}
                          min={getMinStartDate(recurringFrequency)}
                          disabled={
                            recurringFrequency === "every_n_days" &&
                            !recurringInterval
                          }
                        />
                      </TextField>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Ends On
                      </span>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText=""
                        icon={<FeatherCalendar />}
                      >
                        <TextField.Input
                          type="date"
                          placeholder="Select end date"
                          value={recurringEndDate}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setRecurringEndDate(event.target.value);
                            clearFieldError("recurringEndDate");
                          }}
                          disabled={recurringNeverExpires}
                          min={new Date().toISOString().split("T")[0]}
                        />
                      </TextField>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 flex-none rounded-md border border-solid border-neutral-300"
                      checked={recurringNeverExpires}
                      onChange={(e) =>
                        setRecurringNeverExpires(e.target.checked)
                      }
                    />
                    <span className="text-body font-body text-default-font">
                      Never expires
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-6">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Line Items
            </span>
            <div
              className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border"
              style={{ overflow: "visible" }}
            >
              <div className="flex items-center gap-3 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-3 min-w-full">
                <div className="flex w-8 flex-none items-center">
                  <span className="text-body-bold font-body-bold text-default-font">
                    #
                  </span>
                </div>
                <div className="flex w-80 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Account*
                  </span>
                </div>
                <div className="flex w-52 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Description{isApArJournal ? "*" : ""}
                  </span>
                  {isApArJournal && (
                    <span className="mt-0.5 text-[10px] leading-3 text-subtext-color">
                      Max {AP_AR_DESCRIPTION_MAX_LENGTH} characters
                    </span>
                  )}
                </div>
                <div className="flex w-40 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Contact ({currencySymbol})
                  </span>
                </div>
                {showBillInvoiceColumn && (
                  <div className="flex w-40 flex-none items-start">
                    <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                      Bill / Invoice
                    </span>
                  </div>
                )}
                <div className="flex w-28 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Debits
                  </span>
                </div>
                <div className="flex w-28 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Credits
                  </span>
                </div>
                <div className="flex w-16 flex-none items-start" />
              </div>

              {lineItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 border-b border-solid ${
                    hasLineItemError(index)
                      ? "border-error-200 bg-error-50"
                      : "border-neutral-100 bg-white"
                  } px-4 py-2.5 min-w-full overflow-visible`}
                >
                  <div className="flex w-8 flex-none items-center">
                    <span className="text-body-bold font-body-bold text-subtext-color text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <div className="w-80 flex-none flex items-center overflow-hidden min-h-[40px]">
                    <AccountNameDropdown
                      key={`${item.id}-${journalMode}`}
                      docType="journal"
                      journalType={journalMode}
                      selectedAccountId={item.account_id}
                      selectedAccountName={item.account_name}
                      onSelect={(account) =>
                        handleJournalAccountSelect(item.id, account)
                      }
                      dropdownClassName="w-[var(--radix-dropdown-menu-trigger-width)] min-w-full"
                    />
                  </div>
                  <div className="flex w-52 flex-none items-center min-h-[40px]">
                    <TextField
                      label=""
                      helpText={errors[`lineItem_${index}_description`]}
                      error={!!errors[`lineItem_${index}_description`]}
                    >
                      <TextField.Input
                        className="text-sm"
                        placeholder={
                          isApArJournal
                            ? `Enter description (max ${AP_AR_DESCRIPTION_MAX_LENGTH})`
                            : "Enter description"
                        }
                        value={item.description}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          handleLineItemChange(
                            item.id,
                            "description",
                            event.target.value,
                          )
                        }
                        disabled={!item.account_id}
                      />
                    </TextField>
                  </div>
                  <div className="w-40 flex-none flex items-center gap-1 overflow-hidden min-h-[40px]">
                    <div className="flex-1 min-w-0">
                      <SearchableDropdown
                        fetchItems={(searchTerm, limit, offset) =>
                          fetchGroupedContacts(
                            item.account_name,
                            searchTerm,
                            limit,
                            offset,
                            item.contact_id,
                            item.contact_name,
                            item.customer_or_vendor as
                              | "customer"
                              | "vendor"
                              | undefined,
                          )
                        }
                        trigger={
                          <Button
                            variant="neutral-secondary"
                            className="w-full justify-between min-h-9 text-sm"
                            iconRight={<FeatherChevronDown />}
                            disabled={!item.account_id}
                            title={
                              getContactDisplayName(item.contact_id, item) !==
                              "Select contact"
                                ? getContactDisplayName(item.contact_id, item)
                                : ""
                            }
                          >
                            <span className="text-left block max-w-full overflow-hidden whitespace-nowrap text-sm">
                              {truncateText(
                                getContactDisplayName(item.contact_id, item),
                                12,
                              )}
                            </span>
                          </Button>
                        }
                        getSearchableText={(contact: any) =>
                          contact.customer_name || contact.vendor_legal_name
                        }
                        renderItem={(contact: any) =>
                          contact.customer_name || contact.vendor_legal_name
                        }
                        getItemKey={(contact: any) =>
                          contact.zoho_customerid || contact.zoho_vendorid
                        }
                        onSelect={(contact: any) => {
                          handleContactChange(item.id, contact);
                        }}
                        selectedKey={item.contact_id}
                        searchPlaceholder="Search contacts..."
                        emptyMessage="No contacts available"
                        noResultsMessage="No contacts match your search"
                        initialLimit={20}
                      />
                    </div>
                    {item.contact_id ? (
                      <IconButton
                        variant="neutral-secondary"
                        size="small"
                        icon={<FeatherX />}
                        onClick={() => handleClearContact(item.id)}
                        title="Clear contact"
                        aria-label="Clear contact"
                        className="flex-none shrink-0"
                      />
                    ) : null}
                  </div>
                  {showBillInvoiceColumn && (
                    <div className="w-40 flex-none flex items-center overflow-hidden min-h-[40px]">
                      {item.account_type === "accounts_payable" &&
                      item.contact_id ? (
                        <SearchableDropdown<{
                          bill_id: string;
                          bill_number?: string;
                          total: number;
                          balance: number;
                          currency_code: string;
                        }>
                          fetchItems={fetchBillsForContact(item.contact_id)}
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              className="w-full justify-between min-h-9 text-sm"
                              iconRight={<FeatherChevronDown />}
                              title={item.reference_display || ""}
                            >
                              <span className="text-left block max-w-full overflow-hidden whitespace-nowrap text-sm">
                                {truncateText(
                                  item.reference_display || "Select bill",
                                  12,
                                )}
                              </span>
                            </Button>
                          }
                          getSearchableText={(b) =>
                            [
                              b.bill_number,
                              b.bill_id,
                              String(b.balance),
                              String(b.total),
                            ]
                              .filter(Boolean)
                              .join(" ")
                          }
                          renderItem={(b) =>
                            `Bill #${b.bill_number || b.bill_id} - ${b.currency_code || ""} ${Number(b.balance ?? b.total).toLocaleString()}`
                          }
                          getItemKey={(b) => b.bill_id}
                          onSelect={(b) => handleReferenceChange(item.id, b)}
                          selectedKey={item.reference_id || undefined}
                          searchPlaceholder="Search bills..."
                          emptyMessage="No bills for this vendor"
                          noResultsMessage="No bills match your search"
                          initialLimit={20}
                        />
                      ) : item.account_type === "accounts_receivable" &&
                        item.contact_id ? (
                        <SearchableDropdown<{
                          invoice_id: string;
                          invoice_number: string;
                          total: number;
                          balance: number;
                          currency_code?: string;
                        }>
                          fetchItems={fetchInvoicesForContact(item.contact_id)}
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              className="w-full justify-between min-h-9 text-sm"
                              iconRight={<FeatherChevronDown />}
                              title={item.reference_display || ""}
                            >
                              <span className="text-left block max-w-full overflow-hidden whitespace-nowrap text-sm">
                                {truncateText(
                                  item.reference_display || "Select invoice",
                                  12,
                                )}
                              </span>
                            </Button>
                          }
                          getSearchableText={(inv) =>
                            [
                              inv.invoice_number,
                              inv.invoice_id,
                              String(inv.balance),
                              String(inv.total),
                            ]
                              .filter(Boolean)
                              .join(" ")
                          }
                          renderItem={(inv) =>
                            `Invoice #${inv.invoice_number || inv.invoice_id} - ${inv.currency_code || ""} ${Number(inv.balance ?? inv.total).toLocaleString()}`
                          }
                          getItemKey={(inv) => inv.invoice_id}
                          onSelect={(inv) =>
                            handleReferenceChange(item.id, inv)
                          }
                          selectedKey={item.reference_id || undefined}
                          searchPlaceholder="Search invoices..."
                          emptyMessage="No invoices for this customer"
                          noResultsMessage="No invoices match your search"
                          initialLimit={20}
                        />
                      ) : null}
                    </div>
                  )}
                  {/* Debits: always show Debit input; for AP + non-INR show Exchange rate below; highlight for AR */}
                  <div
                    className={`flex w-28 flex-none min-h-[40px] ${
                      isApArJournal &&
                      item.account_type === "accounts_payable" &&
                      item.contact_id &&
                      isContactCurrencyDifferentFromJournal(item)
                        ? errors[`lineItem_${index}_exchange_rate`]
                          ? "flex-col items-start rounded-md bg-error-50 p-1.5"
                          : "flex-col items-start"
                        : "items-center"
                    } ${
                      isApArJournal &&
                      item.account_type === "accounts_receivable"
                        ? "rounded-md bg-error-50 p-1.5"
                        : ""
                    }`}
                  >
                    {isApArJournal &&
                    item.account_type === "accounts_payable" &&
                    item.contact_id &&
                    isContactCurrencyDifferentFromJournal(item) &&
                    getContactCurrencyCodeForLineItem(item) ? (
                      <div className="flex w-full flex-col gap-1.5">
                        <div className="flex w-full flex-row flex-wrap items-center gap-2">
                          <span className="text-xs text-subtext-color shrink-0">
                            Enter amount in{" "}
                            {getContactCurrencyCodeForLineItem(item)}
                          </span>
                          <TextField
                            label=""
                            helpText=""
                            className="min-w-0 flex-1 basis-14"
                          >
                            <TextField.Input
                              type="number"
                              placeholder="0.00"
                              value={item.debit}
                              onKeyDown={(e) =>
                                e.key === "-" && e.preventDefault()
                              }
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const v = event.target.value;
                                if (!allowOnlyNonNegative(v)) return;
                                handleLineItemChange(item.id, "debit", v);
                                if (v && parseFloat(v) > 0) {
                                  handleLineItemChange(item.id, "credit", "");
                                }
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              step="0.01"
                              min="0"
                              disabled={!item.account_id}
                              className="text-sm"
                            />
                          </TextField>
                        </div>
                        <div className="flex w-full flex-row flex-wrap items-center gap-2">
                          <span className="text-xs text-subtext-color shrink-0">
                            Exchange rate *
                          </span>
                          <TextField
                            label=""
                            helpText={
                              errors[`lineItem_${index}_exchange_rate`] ?? ""
                            }
                            error={!!errors[`lineItem_${index}_exchange_rate`]}
                            className="min-w-0 flex-1 basis-14"
                          >
                            <TextField.Input
                              type="number"
                              placeholder="e.g. 83.5"
                              value={item.exchange_rate ?? ""}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleLineItemChange(
                                  item.id,
                                  "exchange_rate",
                                  event.target.value,
                                )
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                              step="0.000001"
                              min="0.000001"
                              className="text-sm"
                            />
                          </TextField>
                          {(() => {
                            const amount = parseFloat(item.debit) || 0;
                            const rate =
                              parseFloat(item.exchange_rate ?? "") || 0;
                            if (amount > 0 && rate > 0) {
                              const value = amount * rate;
                              return (
                                <span className="text-[10px] text-subtext-color whitespace-nowrap">
                                  ({amount.toFixed(2)} * {rate.toFixed(2)}) ={" "}
                                  {currencySymbol} {value.toFixed(2)}
                                </span>
                              );
                            }
                            return (
                              <span className="text-[10px] text-subtext-color whitespace-nowrap">
                                Exchange rate * Amount
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <TextField
                        label=""
                        helpText=""
                        className={
                          isApArJournal &&
                          item.account_type === "accounts_receivable"
                            ? "w-full rounded bg-error-50"
                            : "w-full"
                        }
                      >
                        <TextField.Input
                          type="number"
                          placeholder="0.00"
                          value={item.debit}
                          onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const v = event.target.value;
                            if (!allowOnlyNonNegative(v)) return;
                            handleLineItemChange(item.id, "debit", v);
                            if (v && parseFloat(v) > 0) {
                              handleLineItemChange(item.id, "credit", "");
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.01"
                          min="0"
                          disabled={
                            !item.account_id ||
                            (isApArJournal &&
                              item.account_type === "accounts_receivable")
                          }
                          className="text-sm"
                        />
                      </TextField>
                    )}
                  </div>
                  {/* Credits: always show Credit input; for AR + non-INR show Exchange rate below; highlight for AP */}
                  <div
                    className={`flex w-28 flex-none min-h-[40px] ${
                      isApArJournal &&
                      item.account_type === "accounts_receivable" &&
                      item.contact_id &&
                      isContactCurrencyDifferentFromJournal(item)
                        ? errors[`lineItem_${index}_exchange_rate`]
                          ? "flex-col items-start rounded-md bg-error-50 p-1.5"
                          : "flex-col items-start"
                        : "items-center"
                    } ${
                      isApArJournal && item.account_type === "accounts_payable"
                        ? "rounded-md bg-error-50 p-1.5"
                        : ""
                    }`}
                  >
                    {isApArJournal &&
                    item.account_type === "accounts_receivable" &&
                    item.contact_id &&
                    isContactCurrencyDifferentFromJournal(item) &&
                    getContactCurrencyCodeForLineItem(item) ? (
                      <div className="flex w-full flex-col gap-1.5">
                        <div className="flex w-full flex-row flex-wrap items-center gap-2">
                          <span className="text-xs text-subtext-color shrink-0">
                            Enter amount in{" "}
                            {getContactCurrencyCodeForLineItem(item)}
                          </span>
                          <TextField
                            label=""
                            helpText=""
                            className="min-w-0 flex-1 basis-14"
                          >
                            <TextField.Input
                              type="number"
                              placeholder="0.00"
                              value={item.credit}
                              className="text-sm"
                              onKeyDown={(e) =>
                                e.key === "-" && e.preventDefault()
                              }
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const v = event.target.value;
                                if (!allowOnlyNonNegative(v)) return;
                                handleLineItemChange(item.id, "credit", v);
                                if (v && parseFloat(v) > 0) {
                                  handleLineItemChange(item.id, "debit", "");
                                }
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              step="0.01"
                              min="0"
                              disabled={!item.account_id}
                            />
                          </TextField>
                        </div>
                        <div className="flex w-full flex-row flex-wrap items-center gap-2">
                          <span className="text-xs text-subtext-color shrink-0">
                            Exchange rate *
                          </span>
                          <TextField
                            label=""
                            helpText={
                              errors[`lineItem_${index}_exchange_rate`] ?? ""
                            }
                            error={!!errors[`lineItem_${index}_exchange_rate`]}
                            className="min-w-0 flex-1 basis-14"
                          >
                            <TextField.Input
                              type="number"
                              placeholder="e.g. 83.5"
                              value={item.exchange_rate ?? ""}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleLineItemChange(
                                  item.id,
                                  "exchange_rate",
                                  event.target.value,
                                )
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                              step="0.000001"
                              min="0.000001"
                              className="text-sm"
                            />
                          </TextField>
                          {(() => {
                            const amount = parseFloat(item.credit) || 0;
                            const rate =
                              parseFloat(item.exchange_rate ?? "") || 0;
                            if (amount > 0 && rate > 0) {
                              const value = amount * rate;
                              return (
                                <span className="text-[10px] text-subtext-color whitespace-nowrap">
                                  ({amount.toFixed(2)} * {rate.toFixed(2)}) ={" "}
                                  {currencySymbol} {value.toFixed(2)}
                                </span>
                              );
                            }
                            return (
                              <span className="text-[10px] text-subtext-color whitespace-nowrap">
                                Exchange rate * Amount
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <TextField
                        label=""
                        helpText=""
                        className={
                          isApArJournal &&
                          item.account_type === "accounts_payable"
                            ? "w-full rounded bg-error-50"
                            : "w-full"
                        }
                      >
                        <TextField.Input
                          type="number"
                          placeholder="0.00"
                          value={item.credit}
                          className="text-sm"
                          onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const v = event.target.value;
                            if (!allowOnlyNonNegative(v)) return;
                            handleLineItemChange(item.id, "credit", v);
                            if (v && parseFloat(v) > 0) {
                              handleLineItemChange(item.id, "debit", "");
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          step="0.01"
                          min="0"
                          disabled={
                            !item.account_id ||
                            (isApArJournal &&
                              item.account_type === "accounts_payable")
                          }
                        />
                      </TextField>
                    )}
                  </div>
                  <div className="flex w-16 flex-none items-center justify-center min-h-[40px]">
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                        handleRemoveRow(item.id)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button
              className="h-auto w-auto flex-none"
              variant="neutral-tertiary"
              icon={<FeatherPlus />}
              onClick={handleAddRow}
            >
              Add New Row
            </Button>
          </div>

          {/* Error message for balance */}
          {errors.balance && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.balance}
              </span>
            </div>
          )}
          {/* Error when line amount exceeds selected bill/invoice balance */}
          {errors.referenceBalance && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.referenceBalance}
              </span>
            </div>
          )}
          {/* AP/AR journal: at least one line must be AP or AR */}
          {errors.apArLineItems && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.apArLineItems}
              </span>
            </div>
          )}

          <div className="flex w-full items-start justify-end">
            <div className="flex w-96 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
              <span className="text-body-bold font-body-bold text-default-font">
                Summary
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Debits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalDebits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Credits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalCredits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total ({currencySymbol})
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalDebits().toFixed(2)}
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalCredits().toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Difference
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      Math.abs(getDifference()) < 0.01
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {currencySymbol} {Math.abs(getDifference()).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Attachments
            </span>
            <div className="flex items-center gap-3">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*"
              />
              <label htmlFor="file-upload">
                <Button
                  className="h-auto w-auto flex-none"
                  variant="neutral-secondary"
                  iconRight={<FeatherPlus />}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    document.getElementById("file-upload")?.click();
                  }}
                  disabled={attachments.length >= 5}
                >
                  Upload File
                </Button>
              </label>
              <span className="text-caption font-caption text-subtext-color">
                You can upload a maximum of 5 files, 10MB each
              </span>
            </div>
            {attachments.length > 0 && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Uploaded Files ({attachments.length}/5)
                </span>
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex w-full items-center justify-between rounded bg-white px-3 py-2 border border-solid border-neutral-100"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-body font-body text-default-font truncate">
                        {file.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color flex-none">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveFile(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border pt-6">
            <Button variant="neutral-tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            {isApArJournal ? (
              <Button onClick={handleCreateApArJournal} disabled={isLoading}>
                {isLoading ? "Creating..." : "Create AP/AR Journal"}
              </Button>
            ) : (
              <>
                <Button
                  variant="neutral-secondary"
                  onClick={handleSaveAsDraft}
                  disabled={isLoading || isReviewDataLoading || !canCreate}
                >
                  {isLoading ? "Saving..." : "Save as Draft"}
                </Button>
                <Button
                  onClick={handleSaveAndPublish}
                  disabled={isLoading || isReviewDataLoading || !canPushToZoho}
                >
                  {isLoading ? "Publishing..." : saveAndPublishLabel}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Closed month confirm modal - when non-Admin/CFO tries to save in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={setShowClosedMonthConfirmModal}
        entityLabel="Journal"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          const action = pendingClosedMonthAction;
          setPendingClosedMonthAction(null);
          if (!action) return;
          const journalData = prepareJournalData();
          const fieldChanges = calculateFieldChanges();
          const actionCode =
            action === "save_as_draft"
              ? ACTION_CODES.CREATE
              : ACTION_CODES.PUSH_TO_ZOHO;
          const actionType =
            action === "save_as_draft" ? "create" : "push_to_zoho";
          setReviewChangeDetails({
            closed_month: true,
            action_types: [actionType, ACTION_TYPE_MONTH_CLOSE],
            field_changes: fieldChanges,
            raw_json: journalData,
          });
          setReviewActionCode(actionCode);
          setPendingAction(action);
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode={MODULE_CODES.JOURNAL}
        actionCode={reviewActionCode}
        entityType="journal"
        entityId={createdJournalId || 0} // Use created journal ID if available
        changeDetails={reviewChangeDetails || undefined}
        onSuccess={handleReviewModalSuccess}
        skipNavigation={true} // Don't navigate, we'll handle it in onSuccess
        skipReviewRequest={true} // Don't send review request in modal, we'll do it manually after creating journal
      />
    </DefaultPageLayout>
  );
};

export default NewJournalEntry;
